import { createAsyncThunk } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/nextjs";
import { API, Auth } from "aws-amplify";
import axios from "axios";
import { nanoid } from "nanoid";
import { BrandGuidelinesType, BrandVoiceType, ProjectCreateType, ProjectEditType, ProjectInstructionsType, ReAssignBriefType } from "./types";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getFunctionUrlForScraper = () => {
    if (
        process.env.NEXT_PUBLIC_BRANCH === "dev" ||
        process.env.NODE_ENV === "development"
    ) {
        return `https://fev6a7erl4xysyrz7kholsogo40xwzxv.lambda-url.eu-central-1.on.aws/`;
    }
    if (process.env.NEXT_PUBLIC_BRANCH === "staging") {
        return `https://fev6a7erl4xysyrz7kholsogo40xwzxv.lambda-url.eu-central-1.on.aws/`;
    }
    return `https://fnyglsnmpfhs34e3meowae46re0xwfcm.lambda-url.eu-central-1.on.aws/`;
}
const scraperUrl = getFunctionUrlForScraper();
const getCloudFrontUrl = () => {
    if (
        process.env.NEXT_PUBLIC_BRANCH === "dev" ||
        process.env.NODE_ENV === "development"
    ) {
        return "https://py-staging.seowind.io";
    }
    if (process.env.NEXT_PUBLIC_BRANCH === "staging") {
        return "https://py-staging.seowind.io";
    }
    return "https://py.seowind.io";
};
const pyseowindUrl = getCloudFrontUrl();
const BrandGuidelinesUrl = process.env.NEXT_PUBLIC_BRAND_GUIDELINES_URL || "https://vfp2jiuugdx2crpu4kb7j6llyi0erpxl.lambda-url.eu-central-1.on.aws/";

export const createProject = createAsyncThunk(
    "projects/create",
    async (project: ProjectCreateType) => {
        const response = await API.post("projects", "/projects/create", {
            body: {
                ...project,
                projectId: nanoid(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

export const getProjects = createAsyncThunk(
    "projects/requested",
    async () => {
        try {
            const response = await API.get("projects", "/projects/get", {});
            return response;
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error fetching projects:", error);
            return { error: true };
        }
    }
);

export const deleteProject = createAsyncThunk(
    "projects/delete",
    async (id: string) => {

        const response = await API.post("projects", `/projects/delete`, {

            body: {
                projectId: id,
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

export const updateProject = createAsyncThunk(
    "projects/update",
    async (project: ProjectEditType) => {
        const response = await API.post("projects", `/projects/update`, {

            body: {
                ...project,
                updatedAt: new Date().toISOString(),
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

export const getProject = createAsyncThunk(
    "projects/get",
    async (id: string) => {
        try {
            const response = await API.get("projects", `/projects/${id}`, {});
            return response;
        } catch (error) {
            Sentry.captureException(error);
            console.error(`Error fetching project ${id}:`, error);
            return { error: true };
        }
    }
);

export const reassignBrief = createAsyncThunk(
    "projects/reassign",
    async (project: ReAssignBriefType) => {
        const response = await API.post("projects", `/projects/reassignBrief`, {
            body: {
                ...project,
                updatedAt: new Date().toISOString(),
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

export const getProjectBriefs = createAsyncThunk(
    "projects/getBriefs",
    async (id: string) => {
        const response = await API.get("projects", `/projects/${id}/briefs`, {});
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

//save excluded links for a project  using project id
export const saveExcludedLinks = createAsyncThunk(
    "projects/saveExcludedLinks",
    async ({
        excludedLinks,
        projectId
    }: {
        excludedLinks: any;
        projectId: string;
    }
    ) => {
        const response = await API.post("projects", `/projects/saveExcludedLinks`, {
            body: {
                excludedLinks,
                projectId,
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);
//brand Voice calls

export const getBrandVoice = createAsyncThunk(
    "projects/getBrandVoice",
    async ({ brandText }: { brandText: string; }) => {
        const retry = 3;
        let count = 0;
        let response = await API.post("projects", `/projects/getBrandVoice`, {
            body: {
                brandText: brandText,

            },
        });
        while ((response.error || response.brandVoice == null) && count < retry) {
            count++;
            await wait(500);
            response = await API.post("projects", `/projects/getBrandVoice`, {
                body: {
                    brandText: brandText,
                },
            });

        }
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }

);

export const saveBrandVoice = createAsyncThunk(
    "projects/saveBrandVoice",
    async (

        brandVoice: BrandVoiceType
    ) => {

        const response = await API.post("projects", `/projects/saveBrandVoice`, {

            body: {
                ...brandVoice,
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

export const editBrandVoice = createAsyncThunk(
    "projects/editBrandVoice",
    async (
        brandVoice: BrandVoiceType
    ) => {
        const response = await API.post("projects", `/projects/editBrandVoice`, {

            body: {
                ...brandVoice,
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

export const deleteBrandVoice = createAsyncThunk(
    "projects/deleteBrandVoice",
    async (projectId: string) => {
        const response = await API.post("projects", `/projects/deleteBrandVoice`, {

            body: {
                projectId: projectId,
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

//scrape website
export const scrapeWebsite = createAsyncThunk(
    "projects/scrapeWebsite",
    async (url: string) => {
        try {
            // For AWS_IAM auth type, you need to set these environment variables
            // in your .env file or environment configuration

            if (process.env.NEXT_PUBLIC_LAMBDA_AUTH_TYPE === 'AWS_IAM') {
                try {
                    // Make sure these packages are imported
                    const { SignatureV4 } = require('@aws-sdk/signature-v4');
                    const { Sha256 } = require('@aws-crypto/sha256-browser');
                    const { HttpRequest } = require('@aws-sdk/protocol-http');

                    // Parse the Lambda Function URL
                    const lambdaUrl = new URL(scraperUrl);
                    // Create credentials (from environment variables)
                    const credentials = {
                        accessKeyId: process.env.NEXT_PUBLIC_AWS_ACCESS_KEY_ID || '',
                        secretAccessKey: process.env.NEXT_PUBLIC_AWS_SECRET_ACCESS_KEY || '',
                        // sessionToken: process.env.REACT_APP_AWS_SESSION_TOKEN // if using temp credentials
                    };

                    // Create request object
                    const request = new HttpRequest({
                        method: 'POST',
                        hostname: lambdaUrl.hostname,
                        path: lambdaUrl.pathname,
                        headers: {
                            'Content-Type': 'application/json',
                            'host': lambdaUrl.hostname,

                        },
                        body: JSON.stringify({ url }),
                    });

                    // Create signer
                    const signer = new SignatureV4({
                        credentials,
                        region: process.env.NEXT_PUBLIC_AWS_REGION || 'eu-central-1',
                        service: 'lambda',
                        sha256: Sha256,
                    });

                    // Sign the request
                    const signedRequest = await signer.sign(request);

                    // Make the request with the signed headers
                    const response = await axios({
                        url: scraperUrl,
                        method: 'POST',
                        headers: signedRequest.headers,
                        data: JSON.stringify({ url }),
                    });

                    const data = response.data;
                    return data;
                } catch (signingError) {
                    console.error("Error signing request:", signingError);
                    throw signingError;
                }
            } else {
                // If not using AWS_IAM auth, make a regular request
                const response = await axios.post(scraperUrl, { url }, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                const data = response.data;
                return data;
            }
        } catch (error) {
            console.error("Error calling Lambda function URL:", error);
            throw error;
        }
    }
);

export const getTextContent = createAsyncThunk(
    "projects/getTextContent",
    async (url: string) => {
        const response = await API.post("projects", `/projects/getTextContent`, {
            body: { url },
        });
    }
);

//company details calls

export const saveCompanyDetails = createAsyncThunk(
    "projects/saveCompanyDetails",
    async (companyDetails: any) => {
        const response = await API.post("projects", `/projects/saveCompanyDetails`, {

            body: {
                ...companyDetails,
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

export const saveProjectInstructions = createAsyncThunk(
    "projects/saveProjectInstructions",
    async (instructionsData: ProjectInstructionsType) => {
        const response = await API.post("projects", `/projects/saveProjectInstructions`, {
            body: {
                ...instructionsData,
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

// brand style image calls
export const getBrandGuidelines = createAsyncThunk(
    "projects/getBrandGuidelines",
    async ({ images }: { images: any[] }) => {
        try {
            const { SignatureV4 } = require('@aws-sdk/signature-v4');
            const { Sha256 } = require('@aws-crypto/sha256-browser');
            const { HttpRequest } = require('@aws-sdk/protocol-http');

            const lambdaUrl = new URL(BrandGuidelinesUrl);
            const credentials = await Auth.currentCredentials();
            const requestBody = JSON.stringify({ images });

            const request = new HttpRequest({
                method: 'POST',
                hostname: lambdaUrl.hostname,
                path: lambdaUrl.pathname,
                headers: {
                    'Content-Type': 'application/json',
                    'host': lambdaUrl.hostname,
                },
                body: requestBody,
            });

            const signer = new SignatureV4({
                credentials,
                region: process.env.NEXT_PUBLIC_AWS_REGION || 'eu-central-1',
                service: 'lambda',
                sha256: Sha256,
            });

            const signedRequest = await signer.sign(request);
            const { host, ...headersForRequest } = signedRequest.headers;

            const res = await fetch(BrandGuidelinesUrl, {
                method: 'POST',
                headers: headersForRequest,
                body: requestBody,
            });

            return await res.json();
        } catch (error) {
            console.error("Error calling Lambda function URL:", error);
            throw error;
        }
    }
);

export const saveBrandStyleImage = createAsyncThunk(
    "projects/saveBrandStyleImage",
    async ({ files, projectId }: { files: any[], projectId: string }) => {
        try {
            // Create a FormData object to send all files at once
            const formData = new FormData();

            // Add each file to formData
            files.forEach((file, index) => {
                formData.append('files', file);
            });

            // Add projectId to formData
            formData.append('projectId', projectId);

            // Get the Flask API URL
            const flaskApiUrl = pyseowindUrl;

            // Call your Flask microservice API endpoint
            const response = await fetch(`${flaskApiUrl}/article/getBrandGuidelines`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                return {
                    success: false,
                    error: errorData.error || `Server returned ${response.status}`
                };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error uploading brand style images:", error);
            return {
                success: false,
                error: error instanceof Error ? error.message : "Failed to upload images"
            };
        }
    }
);

//save brand style guidelines
export const saveBrandStyleGuidelines = createAsyncThunk(
    "projects/saveBrandStyleGuidelines",
    async (data: BrandGuidelinesType) => {
        const { projectId, brandGuidelines } = data;
        const response = await API.post("projects", `/projects/saveBrandStyleGuidelines`, {
            body: {
                projectId,
                brandGuidelines,
            },
        });
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);

//fetch brand style guidelines
export const getBrandStyleGuidelines = createAsyncThunk(
    "projects/getBrandStyleGuidelines",
    async (projectId: string) => {
        try {
            const response = await API.get("projects", `/projects/getBrandStyleGuidelines/${projectId}`, {});
            // Transform response to ensure we have a consistent data structure
            // The backend returns guidelines in the response, but we want to make sure it's in the expected format
            if (response && response.success && response.guidelines) {
                return {
                    success: true,
                    data: {
                        brandGuidelines: response.guidelines
                    }
                };
            }
            return response;
        } catch (error) {
            console.error("Error fetching brand guidelines:", error);
            return { error: true, message: "Failed to fetch brand guidelines" };
        }
    }
);

//delete brand style guidelines
export const deleteBrandStyleGuidelines = createAsyncThunk(
    "projects/deleteBrandStyleGuidelines",
    async (projectId: string) => {
        const response = await API.del("projects", `/projects/deleteBrandStyleGuidelines/${projectId}`, {});
        if (response.error) {
            Sentry.captureException(response.error);
        }
        return response;
    }
);
